import React from 'react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog';
import { Search, Plus, Settings, AlertTriangle, Eye, EyeOff, Home, } from 'lucide-react';

import {
  listRoles,
  createRole,
  updateRole,
  listAccessLevels,
  Role as ApiRole,
  AccessLevel,
  CreateRoleRequest,
  getRoleMenus,
  toggleRoleMenuAccess,
} from './api';

import RoleModal from './RoleModal';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import PermissionsTab from './components/PermisssionsTab';
import MenuTab from './components/MenuTab';

interface Menu {
  menu_key: string;
  menu_desc: string;
  icon_name: string;
  has_access: boolean;
  display_order: number;
  parent_menu_key?: string;
  submenu?: Menu[];
}

const RolesPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [roles, setRoles] = React.useState<ApiRole[]>([]);
  const [selectedRole, setSelectedRole] = React.useState<ApiRole | null>(null);
  const [accessLevels, setAccessLevels] = React.useState<AccessLevel[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string>('');
  const [searchTerm, setSearchTerm] = React.useState<string>('');

  // Create role modal state
  const [createModalOpen, setCreateModalOpen] = React.useState<boolean>(false);

  // Permission update state
  const [updatingPermissions, setUpdatingPermissions] = React.useState<boolean>(false);
  const [updatingAccessLevel, setUpdatingAccessLevel] = React.useState<boolean>(false);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = React.useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => { }
  });

  const [activeTab, setActiveTab] = React.useState<'permissions' | 'menu'>('permissions');

  const [roleMenus, setRoleMenus] = React.useState<Menu[]>([]);
  const [menusLoading, setMenusLoading] = React.useState<boolean>(false);
  const [menusError, setMenusError] = React.useState<string>('');

  // Fetch roles & access levels
  const fetchData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [rolesResponse, levelsResponse] = await Promise.all([
        listRoles(),
        listAccessLevels()
      ]);
      setRoles(rolesResponse.items || rolesResponse || []);
      setAccessLevels(levelsResponse || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    setBreadcrumbs([{ label: 'Settings', href: '#' }, { label: 'Role Management' }]);
    fetchData();
  }, [fetchData, setBreadcrumbs]);

  // Fetch role menus when a role is selected AND Menu tab is active
  React.useEffect(() => {
    const fetchRoleMenus = async () => {
      if (!selectedRole) {
        setRoleMenus([]);
        return;
      }

      try {
        setMenusLoading(true);
        setMenusError('');
        const response = await getRoleMenus(selectedRole.role_id.toString());
        setRoleMenus(response || []);
      } catch (err: unknown) {
        console.error('Error fetching role menus:', err);
        setRoleMenus([]);
        setMenusError('Failed to fetch menus');
      } finally {
        setMenusLoading(false);
      }
    };

    if (activeTab === 'menu') {
      fetchRoleMenus();
    } else {
      // Clear menus when not on menu tab (optional)
      // setRoleMenus([]);
    }
  }, [selectedRole, activeTab]);

  const filteredRoles = roles.filter(role =>
    ((role as any).role_name || (role as any).role_desc || '')
      .toString()
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const handleRoleSelect = (role: ApiRole) => {
    setSelectedRole(role);
  };

  const handlePermissionToggle = (permKey: string) => {
    if (!selectedRole) return;

    const permission = selectedRole.permissions.find((p: any) => p.perm_key === permKey);
    if (!permission) return;

    const action = permission.allowed ? 'remove' : 'grant';
    
    setConfirmDialog({
      open: true,
      title: 'Confirm Permission Change',
      description: `Are you sure you want to ${action} the "${permission.perm_desc}" permission for the role "${selectedRole.role_name}"?`,
      onConfirm: () => executePermissionToggle(permKey)
    });
  };

  const executePermissionToggle = async (permKey: string) => {
    if (!selectedRole) return;

    setUpdatingPermissions(true);
    try {
      const updatedPermissions: Record<string, boolean> = {};
      selectedRole.permissions.forEach((perm: any) => {
        updatedPermissions[perm.perm_key] = perm.perm_key === permKey ? !perm.allowed : perm.allowed;
      });

      const updatedRole = await updateRole(selectedRole.role_id, {
        permissions: updatedPermissions
      });

      // Update local state
      setRoles(prev => prev.map(role =>
        role.role_id === selectedRole.role_id ? updatedRole : role
      ));
      setSelectedRole(updatedRole);
    } catch (err: unknown) {
      console.error('Error updating permission:', err);
    } finally {
      setUpdatingPermissions(false);
      setConfirmDialog(prev => ({ ...prev, open: false }));
    }
  };

  const handleCreateRole = async (data: CreateRoleRequest) => {
    await createRole(data);
    await fetchData();
  };

  const handleAccessLevelChange = (newAccessLevelId: string) => {
    if (!selectedRole) return;

    const currentLevel = selectedRole.access_level_id ?
      accessLevels.find(level => level.id === selectedRole.access_level_id)?.name || 'Unknown' :
      'All';

    const newLevel = newAccessLevelId === 'all' ?
      'All' :
      accessLevels.find(level => level.id === Number(newAccessLevelId))?.name || 'Unknown';

    setConfirmDialog({
      open: true,
      title: 'Confirm Access Level Change',
      description: `Are you sure you want to change the access level for role "${selectedRole.role_name}" from "${currentLevel}" to "${newLevel}"?`,
      onConfirm: () => executeAccessLevelChange(newAccessLevelId)
    });
  };

  const executeAccessLevelChange = async (newAccessLevelId: string) => {
    if (!selectedRole) return;

    setUpdatingAccessLevel(true);
    try {
      const updatedRole = await updateRole(selectedRole.role_id, {
        access_level_id: newAccessLevelId === 'all' ? null : Number(newAccessLevelId)
      });

      // Update local state
      setRoles(prev => prev.map(role =>
        role.role_id === selectedRole.role_id ? updatedRole : role
      ));
      setSelectedRole(updatedRole);
    } catch (err: unknown) {
      console.error('Error updating access level:', err);
    } finally {
      setUpdatingAccessLevel(false);
      setConfirmDialog(prev => ({ ...prev, open: false }));
    }
  };

  // --- Menu tab handlers ---
  const handleToggleAccess = async (menuKey: string) => {
    if (!selectedRole) return;

    try {
      await toggleRoleMenuAccess(selectedRole.role_id.toString(), menuKey);
      const response = await getRoleMenus(selectedRole.role_id.toString());
      setRoleMenus(response || []);
    } catch (err) {
      console.error("Error toggling menu access:", err);
    }
  };

  const iconMap: Record<string, any> = {
    home: Home,
    settings: Settings,
    menu: Home,
    default: Settings,
  };

  const renderMenuItem = (menu: Menu, isSubmenu = false) => {
    const IconComponent =
      iconMap[menu.icon_name?.toLowerCase()] || iconMap.default;

    return (
      <div
        key={menu.menu_key}
        className={`flex items-center justify-between p-3 rounded-lg border transition-all ${isSubmenu ? 'ml-6 bg-gray-50' : 'bg-white'
          } border-gray-200 hover:border-gray-300`}
      >
        <div className="flex items-center space-x-3">
          <IconComponent className="w-4 h-4 text-gray-500" />
          <span className="font-medium text-sm text-gray-700">
            {menu.menu_desc}
          </span>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleToggleAccess(menu.menu_key)}
            className={`p-1 rounded hover:bg-gray-100 ${menu.has_access ? 'text-green-600' : 'text-red-500'
              }`}
          >
            {menu.has_access ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
        </div>
      </div>
    );
  };


  return (
    <div className="space-y-6 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-6 sm:px-8">
      <div className="flex h-[calc(100vh-100px)] gap-6 min-h-0">
        <div className="w-1/3 flex flex-col min-h-0">
          <Card className="flex-1 flex flex-col min-h-0">
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-medium">Roles</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col min-h-0 space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search roles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Create Role Button */}
              <Button className="w-full" variant="outline" size="sm" onClick={() => setCreateModalOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Role
              </Button>

              {/* Roles List */}
              <div className="space-y-2 flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : error ? (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                ) : filteredRoles.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    No roles found
                  </div>
                ) : (
                  filteredRoles.map((role) => (
                    <div
                      key={(role as any).role_id}
                      onClick={() => handleRoleSelect(role)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedRole?.role_id === (role as any).role_id
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-border hover:border-primary/50 hover:bg-muted/50'
                        }`}
                    >
                      <div className="flex items-center space-x-2">
                        <Settings className="w-4 h-4" />
                        <span className="font-medium text-sm">
                          {(role as any).role_name || (role as any).role_desc}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Tabs: Menu | Permissions */}
        <div className="flex-1 flex flex-col min-h-0">
          <Card className="flex-1 flex flex-col min-h-0">
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-medium flex items-center justify-between">
                <div>
                  {selectedRole ? (
                    <>Role Settings - {((selectedRole as any).role_name || (selectedRole as any).role_desc)}</>
                  ) : (
                    'Select a Role'
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setActiveTab('menu')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition ${activeTab === 'menu' ? 'bg-primary/10 border border-primary text-primary' : 'bg-transparent text-muted-foreground'
                      }`}
                  >
                    Menu
                  </button>
                  <button
                    onClick={() => setActiveTab('permissions')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition ${activeTab === 'permissions' ? 'bg-primary/10 border border-primary text-primary' : 'bg-transparent text-muted-foreground'
                      }`}
                  >
                    Permissions
                  </button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto min-h-0">
              {!selectedRole ? (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  <div className="text-center">
                    <Settings className="w-8 h-8 mx-auto mb-4 text-muted-foreground/50" />
                    <p className="text-sm">Select a role from the left to view and edit settings</p>
                  </div>
                </div>
              ) : activeTab === "permissions" ? (
                <PermissionsTab
                  selectedRole={selectedRole}
                  accessLevels={accessLevels}
                  updatingAccessLevel={updatingAccessLevel}
                  updatingPermissions={updatingPermissions}
                  handleAccessLevelChange={handleAccessLevelChange}
                  handlePermissionToggle={handlePermissionToggle}
                />
              ) : (
                <MenuTab
                  menusLoading={menusLoading}
                  menusError={menusError}
                  roleMenus={roleMenus}
                  renderMenuItem={renderMenuItem}
                />
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create Role Modal */}
      <RoleModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onSubmit={handleCreateRole}
        availablePermissions={roles.length > 0 ? roles[0].permissions : []}
        accessLevels={accessLevels}
      />

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog(prev => ({ ...prev, open: false }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {confirmDialog.title}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog.description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDialog.onConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RolesPage;
